import { useState } from 'react';
import Dialogo from '../../components/ui/Dialogo';
import { btnPrimario, btnSecundario, campo, pesos } from '../../components/ui/estilos';
import { cajaService, facturasService } from '../../services/api';

const n = (v: any) => parseFloat(v || 0) || 0;
const pastilla = (on: boolean): React.CSSProperties => ({
  height: 48, padding: '0 18px', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: 'pointer',
  background: on ? 'var(--brand)' : 'var(--surface)', color: on ? 'var(--on-brand)' : 'var(--ink)', border: on ? 'none' : '1px solid var(--line-strong)',
});
const etiqueta: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: 'var(--muted)' };
const pie = (onCancelar: () => void, onOk: () => void, ok: string, deshabilitado = false) => (
  <div style={{ display: 'flex', gap: 10 }}>
    <button onClick={onCancelar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
    <button onClick={onOk} disabled={deshabilitado} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16, opacity: deshabilitado ? .5 : 1 }}>{ok}</button>
  </div>
);

/* ─── Corregir con qué se cobró (el total y los productos no se tocan) ─── */
export function DialogoMetodo({ t, metodos, etiquetaMetodo, onListo, onCerrar }: {
  t: any; metodos: { clave: string; nombre: string }[]; etiquetaMetodo: (c: string) => string;
  onListo: () => void; onCerrar: () => void;
}) {
  const aCubrir = +(n(t.monto_total) + n(t.propina)).toFixed(2);
  const [metodo, setMetodo] = useState<string>(t.metodo || metodos[0]?.clave);
  const [mixto, setMixto] = useState(!!t.metodo_2);
  const [metodo2, setMetodo2] = useState<string>(t.metodo_2 || '');
  const [monto1, setMonto1] = useState(t.metodo_2 ? String(t.monto_metodo_1 ?? '') : aCubrir.toFixed(2));
  const [propinaMetodo, setPropinaMetodo] = useState<string>(t.propina_metodo || t.metodo || '');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const m2 = +(aCubrir - n(monto1)).toFixed(2);
  const invalido = mixto && (!metodo2 || metodo2 === metodo || n(monto1) <= 0 || m2 <= 0);

  const guardar = async () => {
    setGuardando(true); setError('');
    try {
      await cajaService.cambiarMetodoPago(t.id, {
        metodo, metodo_2: mixto ? metodo2 : null,
        monto_metodo_1: mixto ? n(monto1) : null, monto_metodo_2: mixto ? m2 : null,
        propina_metodo: propinaMetodo || null,
      });
      onListo();
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo corregir el método.'); }
    finally { setGuardando(false); }
  };

  return (
    <Dialogo titulo="Corregir método de pago" onCerrar={onCerrar} ancho={460}>
      <div style={{ fontSize: 15, color: 'var(--muted)' }}>{pesos(n(t.monto_total))}{n(t.propina) > 0 ? ` + ${pesos(n(t.propina))} de propina` : ''} · solo cambia con qué se cobró.</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {metodos.map(m => (
          <button key={m.clave} onClick={() => { setMetodo(m.clave); setPropinaMetodo(m.clave); if (metodo2 === m.clave) setMetodo2(''); }} aria-pressed={metodo === m.clave} style={pastilla(metodo === m.clave)}>{m.nombre}</button>
        ))}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={mixto} style={{ width: 22, height: 22, accentColor: 'var(--brand-texto)' }}
          onChange={e => { setMixto(e.target.checked); setMonto1((e.target.checked ? aCubrir / 2 : aCubrir).toFixed(2)); setMetodo2(e.target.checked ? (metodos.find(m => m.clave !== metodo)?.clave || '') : ''); }} />
        Se pagó con dos métodos
      </label>
      {mixto && <>
        <label style={etiqueta}>Con {etiquetaMetodo(metodo)} se pagaron
          <input type="number" min="0" inputMode="decimal" value={monto1} onChange={e => setMonto1(e.target.value)} style={{ ...campo, marginTop: 6 }} />
        </label>
        <div style={etiqueta}>El resto ({pesos(m2)}) con</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {metodos.filter(m => m.clave !== metodo).map(m => <button key={m.clave} onClick={() => setMetodo2(m.clave)} aria-pressed={metodo2 === m.clave} style={pastilla(metodo2 === m.clave)}>{m.nombre}</button>)}
        </div>
        {m2 <= 0 && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>Deja algo para el segundo método.</div>}
      </>}
      {n(t.propina) > 0 && <>
        <div style={etiqueta}>La propina se dejó con</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[metodo, mixto ? metodo2 : ''].filter(Boolean).map(c => <button key={c} onClick={() => setPropinaMetodo(c)} aria-pressed={propinaMetodo === c} style={pastilla(propinaMetodo === c)}>{etiquetaMetodo(c)}</button>)}
        </div>
      </>}
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {pie(onCerrar, guardar, guardando ? 'Guardando…' : 'Guardar', guardando || invalido)}
    </Dialogo>
  );
}

/* ─── Facturar (CFDI 4.0 vía Facturama; la cola de timbrado reintenta si falla) ─── */
const REGIMENES = [
  ['601', 'General de Ley Personas Morales'], ['603', 'Personas Morales sin fines de lucro'], ['605', 'Sueldos y Salarios'],
  ['606', 'Arrendamiento'], ['608', 'Demás ingresos'], ['612', 'Personas Físicas con Actividades Empresariales'],
  ['621', 'Incorporación Fiscal'], ['626', 'RESICO'],
];
const USOS = [['G01', 'Adquisición de mercancías'], ['G03', 'Gastos en general'], ['P01', 'Por definir']];
const RFC = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;

export function DialogoFactura({ t, config, onListo, onCerrar }: { t: any; config: Record<string, string>; onListo: (f: any) => void; onCerrar: () => void }) {
  const habilitados = (config.facturacion_usos_habilitados || 'G03').split(',').filter(Boolean);
  const usos = USOS.filter(([c]) => habilitados.includes(c));
  const [f, setF] = useState({ rfc_receptor: '', razon_social_receptor: '', regimen_fiscal_receptor: '626', codigo_postal_receptor: '', uso_cfdi: config.facturacion_uso_cfdi_default || usos[0]?.[0] || 'G03', email: '' });
  const [error, setError] = useState('');
  const [timbrando, setTimbrando] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF(p => ({ ...p, [k]: k === 'rfc_receptor' ? e.target.value.toUpperCase().trim() : e.target.value }));

  const timbrar = async () => {
    if (!RFC.test(f.rfc_receptor)) { setError('El RFC no tiene un formato válido.'); return; }
    if (!f.razon_social_receptor.trim()) { setError('Captura la razón social.'); return; }
    if (!/^\d{5}$/.test(f.codigo_postal_receptor)) { setError('El código postal fiscal lleva 5 dígitos.'); return; }
    setTimbrando(true); setError('');
    try {
      const { data } = await facturasService.crear({ pago_id: t.id, ...f, razon_social_receptor: f.razon_social_receptor.trim(), email: f.email.trim() || undefined });
      onListo(data);
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo timbrar la factura.'); }
    finally { setTimbrando(false); }
  };

  return (
    <Dialogo titulo={`Facturar ${pesos(n(t.monto_total))}`} onCerrar={onCerrar} ancho={480}>
      <input placeholder="RFC" value={f.rfc_receptor} onChange={set('rfc_receptor')} maxLength={13} style={campo} aria-label="RFC" />
      <input placeholder="Razón social" value={f.razon_social_receptor} onChange={set('razon_social_receptor')} maxLength={200} style={campo} aria-label="Razón social" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <input placeholder="C.P. fiscal" inputMode="numeric" value={f.codigo_postal_receptor} onChange={set('codigo_postal_receptor')} maxLength={5} style={campo} aria-label="Código postal fiscal" />
        <select value={f.uso_cfdi} onChange={set('uso_cfdi')} style={campo} aria-label="Uso de CFDI">{usos.map(([c, l]) => <option key={c} value={c}>{c} · {l}</option>)}</select>
      </div>
      <select value={f.regimen_fiscal_receptor} onChange={set('regimen_fiscal_receptor')} style={campo} aria-label="Régimen fiscal">
        {REGIMENES.map(([c, l]) => <option key={c} value={c}>{c} · {l}</option>)}
      </select>
      <input type="email" placeholder="Correo para enviar la factura (opcional)" value={f.email} onChange={set('email')} style={campo} aria-label="Correo" />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {pie(onCerrar, timbrar, timbrando ? 'Timbrando…' : 'Generar CFDI', timbrando)}
    </Dialogo>
  );
}
